/**
 * Performance monitoring utility for TacticsBoard
 * Tracks metrics for UI operations and rendering
 */

import logger from './logger';

// Constants
const METRICS = {
  FPS: 'fps',
  MEMORY: 'memory',
  RENDER_TIME: 'renderTime',
  INTERACTION_TIME: 'interactionTime',
  LOAD_TIME: 'loadTime',
  ANIMATION_SMOOTHNESS: 'animationSmoothness',
};

// Store for performance metrics
const metrics = {
  [METRICS.FPS]: [],
  [METRICS.MEMORY]: [],
  [METRICS.RENDER_TIME]: [],
  [METRICS.INTERACTION_TIME]: [],
  [METRICS.LOAD_TIME]: [],
  [METRICS.ANIMATION_SMOOTHNESS]: [],
};

// Configuration
let config = {
  enabled: process.env.NODE_ENV !== 'production',
  sampleRate: 0.1, // Only sample 10% of metrics in production
  maxSamples: 100, // Keep only last 100 samples
  reportingEndpoint: null,
  reportingInterval: 30000, // 30 seconds
  debugMode: false,
};

// Track if a reporting interval is active
let reportingIntervalId = null;

/**
 * Configure the performance monitor
 * @param {Object} options - Configuration options
 */
export const configure = (options = {}) => {
  config = { ...config, ...options };
  
  // Setup or clear reporting interval based on configuration
  if (reportingIntervalId) {
    clearInterval(reportingIntervalId);
    reportingIntervalId = null;
  }
  
  if (config.enabled && config.reportingEndpoint) {
    reportingIntervalId = setInterval(reportMetrics, config.reportingInterval);
  }
};

/**
 * Check if metric should be sampled based on sample rate
 * @returns {boolean} - Whether to sample this metric
 */
const shouldSample = () => {
  if (!config.enabled) return false;
  if (config.sampleRate >= 1) return true;
  return Math.random() < config.sampleRate;
};

/**
 * Record a performance metric
 * @param {string} metricName - Metric name from METRICS constant
 * @param {number} value - Metric value
 * @param {Object} context - Additional context
 */
export const recordMetric = (metricName, value, context = {}) => {
  if (!shouldSample() || !metrics[metricName]) return;
  
  const timestamp = Date.now();
  
  // Add metric to history, with timestamp
  metrics[metricName].push({
    value,
    timestamp,
    ...context,
  });
  
  // Trim history if needed
  if (metrics[metricName].length > config.maxSamples) {
    metrics[metricName] = metrics[metricName].slice(-config.maxSamples);
  }
  
  // Log in debug mode
  if (config.debugMode) {
    logger.debug(`Perf metric: ${metricName}`, { value, timestamp, ...context });
  }
};

/**
 * Report metrics to configured endpoint
 */
export const reportMetrics = async () => {
  if (!config.enabled || !config.reportingEndpoint) return;
  
  try {
    // Aggregate metrics for reporting
    const aggregatedMetrics = {};
    
    // For each metric type, calculate stats if we have data
    Object.keys(metrics).forEach(metricName => {
      const samples = metrics[metricName];
      if (!samples.length) return;
      
      const values = samples.map(s => s.value);
      
      aggregatedMetrics[metricName] = {
        avg: values.reduce((sum, val) => sum + val, 0) / values.length,
        min: Math.min(...values),
        max: Math.max(...values),
        p90: percentile(values, 90),
        count: values.length,
        timestamp: Date.now()
      };
    });
    
    // Only send if we have data
    if (Object.keys(aggregatedMetrics).length > 0) {
      // Add browser and environment info
      const payload = {
        metrics: aggregatedMetrics,
        userAgent: navigator.userAgent,
        url: window.location.href,
        screenSize: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        timestamp: Date.now()
      };
      
      // Send metrics to endpoint
      await fetch(config.reportingEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        // Don't block the UI while sending metrics
        keepalive: true
      });
      
      // Clear metrics after reporting
      Object.keys(metrics).forEach(key => {
        metrics[key] = [];
      });
      
      if (config.debugMode) {
        logger.debug('Metrics reported', payload);
      }
    }
  } catch (error) {
    logger.error('Failed to report performance metrics', error);
  }
};

/**
 * Calculate percentile value from array of numbers
 * @param {Array<number>} values - Array of values
 * @param {number} p - Percentile (0-100)
 * @returns {number} - Percentile value
 */
const percentile = (values, p) => {
  if (!values.length) return 0;
  
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[index];
};

/**
 * Measure frame rate over time
 * @param {number} duration - Duration in ms to measure
 * @returns {Promise<number>} - Average FPS
 */
export const measureFPS = (duration = 1000) => {
  return new Promise(resolve => {
    let frames = 0;
    let lastTime = window.performance.now();
    
    const countFrame = () => {
      frames++;
      const now = window.performance.now();
      const elapsed = now - lastTime;
      
      if (elapsed < duration) {
        requestAnimationFrame(countFrame);
      } else {
        const fps = (frames / elapsed) * 1000;
        recordMetric(METRICS.FPS, fps);
        resolve(fps);
      }
    };
    
    requestAnimationFrame(countFrame);
  });
};

/**
 * Measure time taken by a function
 * @param {Function} fn - Function to measure
 * @param {string} metricName - Metric name
 * @param {Object} context - Additional context
 * @returns {Promise<any>} - Result of the function
 */
export const measure = async (fn, metricName, context = {}) => {
  if (!shouldSample()) {
    return fn();
  }
  
  const start = window.performance.now();
  try {
    return await fn();
  } finally {
    const duration = window.performance.now() - start;
    recordMetric(metricName, duration, context);
  }
};

/**
 * Create a React component performance wrapper
 * @param {string} componentName - Name of the component
 * @returns {Object} - Performance measurement hooks
 */
export const createComponentMonitor = (componentName) => {
  return {
    onRenderStart: () => {
      return window.performance.now();
    },
    onRenderEnd: (startTime) => {
      const duration = window.performance.now() - startTime;
      recordMetric(METRICS.RENDER_TIME, duration, { component: componentName });
      return duration;
    },
    onInteractionStart: () => {
      return window.performance.now();
    },
    onInteractionEnd: (startTime, interactionType) => {
      const duration = window.performance.now() - startTime;
      recordMetric(METRICS.INTERACTION_TIME, duration, { 
        component: componentName,
        interaction: interactionType
      });
      return duration;
    }
  };
};

/**
 * Get memory usage if available
 * @returns {Object|null} - Memory usage stats or null
 */
export const getMemoryInfo = () => {
  if (performance && performance.memory) {
    const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = performance.memory;
    
    recordMetric(METRICS.MEMORY, usedJSHeapSize / 1048576, { // Convert to MB
      heapSizeLimit: jsHeapSizeLimit / 1048576,
      totalHeapSize: totalJSHeapSize / 1048576
    });
    
    return {
      usedMB: usedJSHeapSize / 1048576,
      totalMB: totalJSHeapSize / 1048576,
      limitMB: jsHeapSizeLimit / 1048576
    };
  }
  
  return null;
};

/**
 * Initialize load time measurement 
 * Called when the app starts loading
 */
export const initLoadMeasurement = () => {
  // Use navigation timing API if available
  if (performance && performance.timing) {
    window.addEventListener('load', () => {
      // Wait for a bit to ensure everything is loaded
      setTimeout(() => {
        const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
        recordMetric(METRICS.LOAD_TIME, loadTime);
      }, 0);
    });
  }
};

/**
 * Get current high-resolution time
 * @returns {number} - Current time in milliseconds
 */
export const now = () => {
  return window.performance.now();
};

export default {
  configure,
  recordMetric,
  reportMetrics,
  measureFPS,
  measure,
  createComponentMonitor,
  getMemoryInfo,
  initLoadMeasurement,
  now, // Add the now function to the exported object
  METRICS
};
